import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  FormControlLabel,
  LinearProgress,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import NotesIcon from "@mui/icons-material/Notes";
import ContentPasteIcon from "@mui/icons-material/ContentPaste";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import UndoIcon from "@mui/icons-material/Undo";
import RedoIcon from "@mui/icons-material/Redo";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import PrintIcon from "@mui/icons-material/Print";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import InsightsIcon from "@mui/icons-material/Insights";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import FlagIcon from "@mui/icons-material/Flag";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AutoStoriesIcon from "@mui/icons-material/AutoStories";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import SpeedIcon from "@mui/icons-material/Speed";
import Footer from "../components/Footer";
import { updateMetaTags } from "../utils/seo";

const HIGHLIGHT_COLORS = {
  repeated: {
    soft: "rgba(59, 130, 246, 0.24)",
    strong: "#2563eb",
  },
  keyword: {
    soft: "rgba(34, 197, 94, 0.24)",
    strong: "#16a34a",
  },
  filler: {
    soft: "rgba(245, 158, 11, 0.24)",
    strong: "#d97706",
  },
  longSentence: {
    soft: "rgba(239, 68, 68, 0.14)",
    strong: "#dc2626",
  },
} as const;

const WORD_CHAR_CLASS = "A-Za-zÀ-ÖØ-öø-ÿ0-9\\u0900-\\u097F";
const WORD_REGEX = new RegExp(
  `[${WORD_CHAR_CLASS}]+(?:['’-][${WORD_CHAR_CLASS}]+)*`,
  "g",
);
const WORD_TOKEN_REGEX = new RegExp(
  `^[${WORD_CHAR_CLASS}]+(?:['’-][${WORD_CHAR_CLASS}]+)*$`,
);
const SENTENCE_REGEX = /[^.!?\n]+[.!?]+|[^.!?\n]+$/g;
const EDGE_NON_WORD_REGEX = new RegExp(
  `^[^${WORD_CHAR_CLASS}]+|[^${WORD_CHAR_CLASS}]+$`,
  "g",
);
const SENTENCE_CASE_REGEX = new RegExp(
  "(^\\s*[a-zà-öø-ÿ\\u0900-\\u097F]|[.!?]\\s+[a-zà-öø-ÿ\\u0900-\\u097F])",
  "gi",
);
const DEVANAGARI_REGEX = /[\u0900-\u097F]/;
const TOKEN_SPLIT_REGEX = new RegExp(`(\\s+|[^${WORD_CHAR_CLASS}'’-]+)`);
const REPEATED_ADJACENT_REGEX = new RegExp(
  `\\b([${WORD_CHAR_CLASS}]+)\\b\\s+\\b\\1\\b`,
  "gi",
);
const MAX_HISTORY = 250;
const DEFAULT_TARGET_WORDS = 1000;
const LONG_SENTENCE_THRESHOLD = 25;
const READING_WPM = 225;
const SPEAKING_WPM = 130;
const DRAFT_STORAGE_KEY = "portfolio-word-counter-draft";
const DRAFT_TIMESTAMP_KEY = "portfolio-word-counter-draft-saved-at";

const STOP_WORDS = new Set([
  "a",
  "about",
  "above",
  "against",
  "all",
  "am",
  "an",
  "because",
  "been",
  "before",
  "being",
  "below",
  "between",
  "both",
  "but",
  "by",
  "can",
  "could",
  "did",
  "do",
  "does",
  "doing",
  "down",
  "during",
  "each",
  "few",
  "for",
  "from",
  "further",
  "had",
  "has",
  "have",
  "having",
  "he",
  "her",
  "here",
  "hers",
  "herself",
  "him",
  "himself",
  "his",
  "how",
  "i",
  "if",
  "in",
  "into",
  "is",
  "it",
  "its",
  "itself",
  "just",
  "me",
  "more",
  "most",
  "my",
  "myself",
  "no",
  "nor",
  "not",
  "now",
  "of",
  "off",
  "on",
  "once",
  "only",
  "or",
  "other",
  "our",
  "ours",
  "ourselves",
  "out",
  "over",
  "own",
  "same",
  "she",
  "should",
  "so",
  "some",
  "such",
  "than",
  "that",
  "the",
  "their",
  "theirs",
  "them",
  "themselves",
  "then",
  "there",
  "these",
  "they",
  "this",
  "those",
  "through",
  "to",
  "too",
  "under",
  "until",
  "up",
  "very",
  "was",
  "we",
  "were",
  "what",
  "when",
  "where",
  "which",
  "while",
  "who",
  "whom",
  "why",
  "with",
  "would",
  "you",
  "your",
  "yours",
  "yourself",
  "yourselves",
]);

const FILLER_WORDS = [
  "actually",
  "basically",
  "clearly",
  "definitely",
  "essentially",
  "extremely",
  "fairly",
  "generally",
  "honestly",
  "just",
  "kind of",
  "literally",
  "maybe",
  "perhaps",
  "probably",
  "quite",
  "really",
  "simply",
  "sort of",
  "stuff",
  "things",
  "totally",
  "truly",
  "very",
];

const LANGUAGE_HINTS: Array<{ label: string; hints: string[] }> = [
  {
    label: "English",
    hints: ["the", "and", "that", "with", "this", "from", "have", "will"],
  },
  {
    label: "Spanish",
    hints: ["que", "para", "con", "una", "por", "los", "del", "las"],
  },
  {
    label: "French",
    hints: ["les", "des", "une", "dans", "avec", "pour", "est", "pas"],
  },
  {
    label: "German",
    hints: ["und", "der", "die", "das", "ist", "mit", "nicht", "ein"],
  },
];

const wordCounterFaqItems = [
  {
    question: "Does this word counter send my writing to a server?",
    answer:
      "No. The tool runs entirely client-side in the browser. Counting, readability analysis, keyword density checks, draft saving and export actions all happen locally on your device.",
  },
  {
    question: "Can I paste text or upload a text file?",
    answer:
      "Yes. You can type directly, paste from the clipboard, drag and drop .txt or .md files, or upload a local text file for instant analysis.",
  },
  {
    question: "What writing insights does this tool provide?",
    answer:
      "The page shows word count, character count, sentence and paragraph totals, reading time, speaking time, keyword density, filler words, repeated words, readability scores, goal progress and document structure metrics.",
  },
  {
    question: "Can I transform and export the text?",
    answer:
      "Yes. You can convert text to uppercase, lowercase, title case, sentence case, camelCase and snake_case, remove extra spaces or line breaks, then copy, download or print the result.",
  },
] as const;

const wordCounterUseCases = [
  "Check article, blog and essay length while writing or editing",
  "Measure keyword density for SEO-focused drafts and landing pages",
  "Estimate reading time, speaking time and typing speed for scripts",
  "Clean up pasted notes with text transformation utilities",
  "Review readability, filler words and repeated word usage",
  "Track progress toward a target word count for assignments and content plans",
];

const starterTemplates = [
  {
    label: "Blog intro",
    text: "Writing clear content starts with knowing how your draft reads. This sample paragraph helps you test word count, reading time, keyword density, and readability in a realistic writing workflow.",
  },
  {
    label: "Meeting notes",
    text: "Agenda\n- Review weekly goals\n- Discuss blockers\n- Confirm ownership\n\nNotes\nThe team agreed to prioritise the landing page update and move testing fixes into the next sprint.",
  },
  {
    label: "Essay paragraph",
    text: "Technology changes how people learn, communicate, and work. It increases access to information, but it also creates new responsibilities around focus, privacy, and critical thinking.",
  },
  {
    label: "SEO draft",
    text: "A good SEO article balances keyword relevance with readability. The content should answer the search intent clearly, use headings well, and avoid awkward repetition of the main keyword.",
  },
] as const;

const getWords = (text: string) => text.match(WORD_REGEX) ?? [];

const normalizeWord = (word: string) =>
  word.toLowerCase().replace(EDGE_NON_WORD_REGEX, "");

const splitSentences = (text: string) =>
  (text.match(SENTENCE_REGEX) ?? [])
    .map((sentence) => sentence.trim())
    .filter(Boolean);

const splitParagraphs = (text: string) =>
  text
    .split(/\n\s*\n/g)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);

const formatDecimal = (value: number, maximumFractionDigits = 1) => {
  if (!Number.isFinite(value)) {
    return "0";
  }

  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits,
  }).format(value);
};

const formatPercent = (value: number) => `${formatDecimal(value, 1)}%`;

const formatMinutes = (minutes: number) => {
  if (!Number.isFinite(minutes) || minutes <= 0) {
    return "0 min";
  }

  const totalSeconds = Math.ceil(minutes * 60);
  const minutePart = Math.floor(totalSeconds / 60);
  const secondPart = totalSeconds % 60;

  if (minutePart === 0) {
    return `${secondPart} sec`;
  }

  if (secondPart === 0) {
    return `${minutePart} min`;
  }

  return `${minutePart} min ${secondPart} sec`;
};

const formatDuration = (seconds: number) => {
  const safeSeconds = Math.max(seconds, 0);
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const remainingSeconds = safeSeconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m ${remainingSeconds}s`;
  }

  if (minutes > 0) {
    return `${minutes}m ${remainingSeconds}s`;
  }

  return `${remainingSeconds}s`;
};

const estimateSyllables = (word: string) => {
  const cleaned = normalizeWord(word).replace(/[^a-z]/g, "");

  if (!cleaned) {
    return 1;
  }

  const withoutSilentE = cleaned.endsWith("e") ? cleaned.slice(0, -1) : cleaned;
  const groups = withoutSilentE.match(/[aeiouy]+/g);
  return Math.max(groups?.length ?? 1, 1);
};

const getReadabilityLabel = (score: number) => {
  if (score >= 90) return "Very easy";
  if (score >= 80) return "Easy";
  if (score >= 70) return "Fairly easy";
  if (score >= 60) return "Standard";
  if (score >= 50) return "Fairly difficult";
  if (score >= 30) return "Difficult";
  return "Very difficult";
};

const toTitleCase = (text: string) =>
  text.replace(
    /\w\S*/g,
    (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
  );

const toSentenceCase = (text: string) => {
  const normalized = text.toLowerCase();
  return normalized.replace(SENTENCE_CASE_REGEX, (match) =>
    match.toUpperCase(),
  );
};

const toCamelCase = (text: string) => {
  const words = getWords(text)
    .map((word) => normalizeWord(word))
    .filter(Boolean);
  if (!words.length) return "";

  return words
    .map((word, index) =>
      index === 0
        ? word.toLowerCase()
        : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
    )
    .join("");
};

const toSnakeCase = (text: string) =>
  getWords(text)
    .map((word) => normalizeWord(word))
    .filter(Boolean)
    .join("_");

const removeExtraSpaces = (text: string) =>
  text
    .replace(/[ \t]+/g, " ")
    .replace(/\n{3,}/g, "\n\n")
    .replace(/ *\n */g, "\n")
    .trim();

const removeLineBreaks = (text: string) =>
  text
    .replace(/\s*\n+\s*/g, " ")
    .replace(/\s{2,}/g, " ")
    .trim();

const detectLanguage = (text: string, normalizedWords: string[]) => {
  if (!text.trim()) {
    return "Unknown";
  }

  if (DEVANAGARI_REGEX.test(text)) {
    return "Nepali / Hindi (Devanagari)";
  }

  let bestLanguage = "Unknown";
  let bestScore = 0;

  for (const option of LANGUAGE_HINTS) {
    const score = option.hints.reduce(
      (count, hint) => count + Number(normalizedWords.includes(hint)),
      0,
    );

    if (score > bestScore) {
      bestScore = score;
      bestLanguage = option.label;
    }
  }

  return bestScore >= 2 ? bestLanguage : "Unknown";
};

const escapeRegExp = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const getFillerMatches = (text: string) =>
  FILLER_WORDS.map((term) => {
    const pattern = new RegExp(
      `\\b${escapeRegExp(term).replace(/ /g, "\\s+")}\\b`,
      "gi",
    );
    const matches = text.match(pattern) ?? [];

    return {
      term,
      count: matches.length,
    };
  }).filter((item) => item.count > 0);

const downloadFile = (content: string, extension: "txt" | "md") => {
  const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `word-counter-export.${extension}`;
  link.click();
  URL.revokeObjectURL(url);
};

const printText = (content: string) => {
  const popup = window.open(
    "",
    "_blank",
    "noopener,noreferrer,width=900,height=700",
  );
  if (!popup) {
    return false;
  }

  const escaped = content
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

  popup.document.write(`
    <html>
      <head>
        <title>Word Counter Export</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 32px; line-height: 1.6; white-space: pre-wrap; }
          h1 { margin-bottom: 24px; }
        </style>
      </head>
      <body>
        <h1>Word Counter Export</h1>
        <pre style="white-space: pre-wrap; word-break: break-word;">${escaped}</pre>
      </body>
    </html>
  `);
  popup.document.close();
  popup.focus();
  popup.print();
  return true;
};

const getTokenHighlightStyles = ({
  repeated,
  filler,
  keyword,
}: {
  repeated: boolean;
  filler: boolean;
  keyword: boolean;
}) => {
  const activeColors = [
    repeated ? HIGHLIGHT_COLORS.repeated.soft : null,
    keyword ? HIGHLIGHT_COLORS.keyword.soft : null,
    filler ? HIGHLIGHT_COLORS.filler.soft : null,
  ].filter(Boolean) as string[];

  const borderColors = [
    repeated ? HIGHLIGHT_COLORS.repeated.strong : null,
    keyword ? HIGHLIGHT_COLORS.keyword.strong : null,
    filler ? HIGHLIGHT_COLORS.filler.strong : null,
  ].filter(Boolean) as string[];

  return {
    background:
      activeColors.length <= 1
        ? (activeColors[0] ?? "transparent")
        : `linear-gradient(135deg, ${activeColors
            .map((color, index) => {
              const start = Math.round((index / activeColors.length) * 100);
              const end = Math.round(((index + 1) / activeColors.length) * 100);
              return `${color} ${start}% ${end}%`;
            })
            .join(", ")})`,
    boxShadow: borderColors.length
      ? `inset 0 -2px 0 ${borderColors[0]}${borderColors[1] ? `, inset 0 -5px 0 ${borderColors[1]}` : ""}${borderColors[2] ? `, inset 0 -8px 0 ${borderColors[2]}` : ""}`
      : "none",
  };
};

const buildHighlightPreview = ({
  text,
  repeatedWordSet,
  fillerWordSet,
  keywordSet,
  highlightRepeatedWords,
  highlightFillerWords,
  highlightKeywords,
  highlightLongSentences,
}: {
  text: string;
  repeatedWordSet: Set<string>;
  fillerWordSet: Set<string>;
  keywordSet: Set<string>;
  highlightRepeatedWords: boolean;
  highlightFillerWords: boolean;
  highlightKeywords: boolean;
  highlightLongSentences: boolean;
}) => {
  if (!text.trim()) {
    return (
      <Typography variant="body2" color="text.secondary">
        Highlight preview appears here while you type, paste or upload text.
      </Typography>
    );
  }

  const pieces = text.match(/[^.!?\n]+[.!?]*|\n+/g) ?? [text];

  return pieces.map((piece, pieceIndex) => {
    if (/^\n+$/.test(piece)) {
      return (
        <React.Fragment key={`linebreak-${pieceIndex}`}>
          {piece.split("").map((_character, index) => (
            <br key={`br-${pieceIndex}-${index}`} />
          ))}
        </React.Fragment>
      );
    }

    const sentenceWordCount = getWords(piece).length;
    const isLongSentence =
      highlightLongSentences && sentenceWordCount >= LONG_SENTENCE_THRESHOLD;
    const tokens = piece
      .split(TOKEN_SPLIT_REGEX)
      .filter((token) => token !== "");

    const sentenceNode = tokens.map((token, tokenIndex) => {
      if (/^\s+$/.test(token) || !WORD_TOKEN_REGEX.test(token)) {
        return (
          <React.Fragment key={`token-${pieceIndex}-${tokenIndex}`}>
            {token}
          </React.Fragment>
        );
      }

      const normalized = normalizeWord(token);
      const shouldHighlightRepeated =
        highlightRepeatedWords && repeatedWordSet.has(normalized);
      const shouldHighlightFiller =
        highlightFillerWords && fillerWordSet.has(normalized);
      const shouldHighlightKeyword =
        highlightKeywords && keywordSet.has(normalized);

      if (
        !shouldHighlightRepeated &&
        !shouldHighlightFiller &&
        !shouldHighlightKeyword
      ) {
        return (
          <React.Fragment key={`token-${pieceIndex}-${tokenIndex}`}>
            {token}
          </React.Fragment>
        );
      }

      return (
        <Box
          key={`token-${pieceIndex}-${tokenIndex}`}
          component="mark"
          sx={{
            px: 0.25,
            py: 0.05,
            borderRadius: 0.75,
            ...getTokenHighlightStyles({
              repeated: shouldHighlightRepeated,
              keyword: shouldHighlightKeyword,
              filler: shouldHighlightFiller,
            }),
            color: "inherit",
          }}
        >
          {token}
        </Box>
      );
    });

    if (!isLongSentence) {
      return (
        <React.Fragment key={`piece-${pieceIndex}`}>
          {sentenceNode}
        </React.Fragment>
      );
    }

    return (
      <Box
        key={`piece-${pieceIndex}`}
        component="span"
        sx={{
          backgroundColor: HIGHLIGHT_COLORS.longSentence.soft,
          outline: `1px solid ${HIGHLIGHT_COLORS.longSentence.strong}`,
          borderRadius: 1,
          px: 0.25,
          py: 0.1,
        }}
      >
        {sentenceNode}
      </Box>
    );
  });
};

const WordCounter: React.FC = () => {
  const [text, setText] = useState("");
  const [undoStack, setUndoStack] = useState<string[]>([]);
  const [redoStack, setRedoStack] = useState<string[]>([]);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [saveDraftEnabled, setSaveDraftEnabled] = useState(true);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [targetWords, setTargetWords] = useState(
    DEFAULT_TARGET_WORDS.toString(),
  );
  const [filterStopWords, setFilterStopWords] = useState(true);
  const [highlightRepeatedWords, setHighlightRepeatedWords] = useState(true);
  const [highlightLongSentences, setHighlightLongSentences] = useState(true);
  const [highlightKeywords, setHighlightKeywords] = useState(true);
  const [highlightFillerWords, setHighlightFillerWords] = useState(true);
  const [typingStartedAt, setTypingStartedAt] = useState<number | null>(null);
  const [typingTick, setTypingTick] = useState(Date.now());

  const pageBackground = isDarkMode ? "#0f172a" : "background.default";
  const cardBackground = isDarkMode
    ? "rgba(15, 23, 42, 0.9)"
    : "background.paper";
  const mutedTextColor = isDarkMode
    ? "rgba(226, 232, 240, 0.78)"
    : "text.secondary";
  const borderColor = isDarkMode ? "rgba(148, 163, 184, 0.18)" : "divider";
  const primaryTextColor = isDarkMode ? "#e2e8f0" : "text.primary";

  useEffect(() => {
    updateMetaTags({
      title:
        "Free Word Counter, Text Analyzer & Writing Stats Tool | Shashi Sharma",
      description:
        "Free online word counter with real-time counts, reading time, keyword density, readability score, filler word detection, text transformations, draft saving and client-side privacy.",
      keywords:
        "word counter, character counter, text analyzer, reading time calculator, keyword density checker, readability checker, sentence counter, paragraph counter, online word count tool",
      canonical: `${window.location.origin}/word-counter`,
      ogTitle: "Free Word Counter, Text Analyzer & Writing Stats Tool",
      ogDescription:
        "Count words, characters, sentences and paragraphs in real time. Analyze keyword density, readability, repeated words, reading time and writing goals directly in the browser.",
      ogUrl: `${window.location.origin}/word-counter`,
      ogType: "website",
      ogImage: `${window.location.origin}/favicon.png`,
      twitterCard: "summary",
      twitterTitle: "Free Word Counter, Text Analyzer & Writing Stats Tool",
      twitterDescription:
        "Real-time word count, readability, keyword analysis, text cleanup and local-only processing for private writing workflows.",
      twitterImage: `${window.location.origin}/favicon.png`,
      structuredData: JSON.stringify([
        {
          "@context": "https://schema.org",
          "@type": "WebApplication",
          name: "Free Word Counter",
          applicationCategory: "UtilitiesApplication",
          operatingSystem: "Any",
          url: `${window.location.origin}/word-counter`,
          description:
            "Free client-side word counter and text analyzer with readability scoring, keyword analysis, writing insights, draft saving and export tools.",
          offers: {
            "@type": "Offer",
            price: "0",
            priceCurrency: "USD",
          },
          featureList: [
            "Word count and character count",
            "Reading time and speaking time estimates",
            "Keyword density and repeated word analysis",
            "Readability scoring and filler word detection",
            "Text transformations and local draft saving",
            "Client-side privacy with no server processing",
          ],
        },
        {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: wordCounterFaqItems.map((item) => ({
            "@type": "Question",
            name: item.question,
            acceptedAnswer: {
              "@type": "Answer",
              text: item.answer,
            },
          })),
        },
        {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: "https://www.shashi-sharma.com.np/",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Utilities",
              item: "https://www.shashi-sharma.com.np/word-counter",
            },
            {
              "@type": "ListItem",
              position: 3,
              name: "Word Counter",
              item: "https://www.shashi-sharma.com.np/word-counter",
            },
          ],
        },
      ]),
    });
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const savedDraft = window.localStorage.getItem(DRAFT_STORAGE_KEY);
    const lastSavedAt = window.localStorage.getItem(DRAFT_TIMESTAMP_KEY);

    if (savedDraft) {
      setText(savedDraft);
      setSavedAt(lastSavedAt);
    }
  }, []);

  useEffect(() => {
    if (!saveDraftEnabled || typeof window === "undefined") {
      if (typeof window !== "undefined") {
        window.localStorage.removeItem(DRAFT_STORAGE_KEY);
        window.localStorage.removeItem(DRAFT_TIMESTAMP_KEY);
      }
      setSavedAt(null);
      return;
    }

    window.localStorage.setItem(DRAFT_STORAGE_KEY, text);
    const timestamp = new Date().toISOString();
    window.localStorage.setItem(DRAFT_TIMESTAMP_KEY, timestamp);
    setSavedAt(timestamp);
  }, [saveDraftEnabled, text]);

  useEffect(() => {
    if (!typingStartedAt) {
      return undefined;
    }

    const interval = window.setInterval(() => setTypingTick(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, [typingStartedAt]);

  const updateText = (nextValue: string) => {
    if (nextValue === text) {
      return;
    }

    setUndoStack((current) => [...current.slice(-(MAX_HISTORY - 1)), text]);
    setRedoStack([]);
    setText(nextValue);

    if (nextValue.trim()) {
      setTypingStartedAt((current) => current ?? Date.now());
      setTypingTick(Date.now());
    } else {
      setTypingStartedAt(null);
      setTypingTick(Date.now());
    }
  };

  const analysis = useMemo(() => {
    const normalizedText = text.replace(/\r\n/g, "\n");
    const words = getWords(normalizedText);
    const normalizedWords = words
      .map((word) => normalizeWord(word))
      .filter(Boolean);
    const sentences = splitSentences(normalizedText);
    const paragraphs = splitParagraphs(normalizedText);
    const lines = normalizedText ? normalizedText.split("\n") : [];
    const frequencyMap = normalizedWords.reduce<Record<string, number>>(
      (map, word) => {
        map[word] = (map[word] ?? 0) + 1;
        return map;
      },
      {},
    );

    const filteredKeywordSource = normalizedWords.filter(
      (word) => !filterStopWords || !STOP_WORDS.has(word),
    );
    const keywordMap = filteredKeywordSource.reduce<Record<string, number>>(
      (map, word) => {
        map[word] = (map[word] ?? 0) + 1;
        return map;
      },
      {},
    );

    const keywordEntries = Object.entries(keywordMap)
      .sort(
        (left, right) => right[1] - left[1] || left[0].localeCompare(right[0]),
      )
      .slice(0, 12)
      .map(([word, count]) => ({
        word,
        count,
        percentage: filteredKeywordSource.length
          ? (count / filteredKeywordSource.length) * 100
          : 0,
      }));

    const repeatedWords = Object.entries(frequencyMap)
      .filter(([, count]) => count > 1)
      .sort(
        (left, right) => right[1] - left[1] || left[0].localeCompare(right[0]),
      )
      .slice(0, 12)
      .map(([word, count]) => ({ word, count }));

    const paragraphWordCounts = paragraphs.map(
      (paragraph) => getWords(paragraph).length,
    );
    const sentenceWordCounts = sentences.map(
      (sentence) => getWords(sentence).length,
    );
    const syllableCount = words.reduce(
      (total, word) => total + estimateSyllables(word),
      0,
    );
    const wordLengths = words
      .map((word) => normalizeWord(word).replace(/['’-]/g, "").length)
      .filter(Boolean);
    const uniqueWordCount = new Set(normalizedWords).size;
    const lexicalDiversity = words.length
      ? (uniqueWordCount / words.length) * 100
      : 0;
    const readabilityEase =
      words.length && sentences.length
        ? 206.835 -
          1.015 * (words.length / sentences.length) -
          84.6 * (syllableCount / words.length)
        : 0;
    const readabilityGrade =
      words.length && sentences.length
        ? 0.39 * (words.length / sentences.length) +
          11.8 * (syllableCount / words.length) -
          15.59
        : 0;
    const normalizedUniqueWords = Array.from(new Set(normalizedWords));
    const longestWords = normalizedUniqueWords
      .sort(
        (left, right) =>
          right.length - left.length || left.localeCompare(right),
      )
      .slice(0, 6);
    const shortestWords = normalizedUniqueWords
      .filter((word) => word.length > 0)
      .sort(
        (left, right) =>
          left.length - right.length || left.localeCompare(right),
      )
      .slice(0, 6);
    const repeatedAdjacentWords =
      normalizedText.match(REPEATED_ADJACENT_REGEX) ?? [];
    const fillerMatches = getFillerMatches(normalizedText);

    return {
      normalizedText,
      words,
      normalizedWords,
      wordCount: words.length,
      characterCountWithSpaces: normalizedText.length,
      characterCountWithoutSpaces: normalizedText.replace(/\s/g, "").length,
      sentenceCount: sentences.length,
      paragraphCount: paragraphs.length,
      lineCount: lines.length,
      averageWordLength: words.length
        ? wordLengths.reduce((sum, value) => sum + value, 0) / words.length
        : 0,
      averageSentenceLength: sentences.length
        ? words.length / sentences.length
        : 0,
      averageParagraphLength: paragraphs.length
        ? words.length / paragraphs.length
        : 0,
      readingTimeMinutes: words.length / READING_WPM,
      speakingTimeMinutes: words.length / SPEAKING_WPM,
      uniqueWordCount,
      repeatedWords,
      longestWords,
      shortestWords,
      paragraphWordCounts,
      sentenceWordCounts,
      longestParagraphWords: paragraphWordCounts.length
        ? Math.max(...paragraphWordCounts)
        : 0,
      longestSentenceWords: sentenceWordCounts.length
        ? Math.max(...sentenceWordCounts)
        : 0,
      keywordEntries,
      fillerMatches,
      repeatedAdjacentWords,
      lexicalDiversity,
      readabilityEase,
      readabilityGrade,
      language: detectLanguage(normalizedText, normalizedWords),
      readabilityLabel: getReadabilityLabel(readabilityEase),
      structureSummary: {
        shortParagraphs: paragraphWordCounts.filter(
          (count) => count > 0 && count < 40,
        ).length,
        mediumParagraphs: paragraphWordCounts.filter(
          (count) => count >= 40 && count < 120,
        ).length,
        longParagraphs: paragraphWordCounts.filter((count) => count >= 120)
          .length,
      },
    };
  }, [filterStopWords, text]);

  const targetWordValue = Number(targetWords);
  const goalProgress =
    targetWordValue > 0
      ? Math.min((analysis.wordCount / targetWordValue) * 100, 100)
      : 0;
  const typingDurationSeconds = typingStartedAt
    ? Math.max(Math.floor((typingTick - typingStartedAt) / 1000), 0)
    : 0;
  const typingSpeedWpm =
    typingDurationSeconds > 0
      ? (analysis.wordCount / typingDurationSeconds) * 60
      : 0;

  const repeatedWordSet = useMemo(
    () => new Set(analysis.repeatedWords.map((item) => item.word)),
    [analysis.repeatedWords],
  );
  const fillerWordSet = useMemo(
    () =>
      new Set(analysis.fillerMatches.map((item) => item.term.split(" ")[0])),
    [analysis.fillerMatches],
  );
  const keywordSet = useMemo(
    () => new Set(analysis.keywordEntries.slice(0, 8).map((item) => item.word)),
    [analysis.keywordEntries],
  );

  const highlightPreview = useMemo(
    () =>
      buildHighlightPreview({
        text,
        repeatedWordSet,
        fillerWordSet,
        keywordSet,
        highlightRepeatedWords,
        highlightFillerWords,
        highlightKeywords,
        highlightLongSentences,
      }),
    [
      fillerWordSet,
      highlightFillerWords,
      highlightKeywords,
      highlightLongSentences,
      highlightRepeatedWords,
      keywordSet,
      repeatedWordSet,
      text,
    ],
  );

  const coreStats = [
    { label: "Words", value: analysis.wordCount },
    { label: "Characters", value: analysis.characterCountWithSpaces },
    {
      label: "Characters (no spaces)",
      value: analysis.characterCountWithoutSpaces,
    },
    { label: "Sentences", value: analysis.sentenceCount },
    { label: "Paragraphs", value: analysis.paragraphCount },
    { label: "Lines", value: analysis.lineCount },
    {
      label: "Avg word length",
      value: formatDecimal(analysis.averageWordLength, 2),
    },
    {
      label: "Avg sentence length",
      value: formatDecimal(analysis.averageSentenceLength, 1),
    },
  ];

  const summaryCards = [
    {
      label: "Live words",
      value: formatDecimal(analysis.wordCount, 0),
      meta: `${formatDecimal(analysis.uniqueWordCount, 0)} unique`,
      icon: <TextFieldsIcon color="primary" />,
    },
    {
      label: "Reading time",
      value: formatMinutes(analysis.readingTimeMinutes),
      meta: `${formatMinutes(analysis.speakingTimeMinutes)} speaking`,
      icon: <AccessTimeIcon color="primary" />,
    },
    {
      label: "Readability",
      value: analysis.readabilityLabel,
      meta: `Flesch ${formatDecimal(analysis.readabilityEase, 1)}`,
      icon: <AutoStoriesIcon color="primary" />,
    },
    {
      label: "Typing speed",
      value: `${formatDecimal(typingSpeedWpm, 1)} WPM`,
      meta:
        targetWordValue > 0
          ? `${formatPercent(goalProgress)} goal progress`
          : "Add a target to track progress",
      icon: <SpeedIcon color="primary" />,
    },
  ];

  const longSentenceCount = analysis.sentenceWordCounts.filter(
    (count) => count >= LONG_SENTENCE_THRESHOLD,
  ).length;

  const smartSuggestions = [
    analysis.wordCount === 0
      ? "Start with a sample template, paste text, or upload a .txt/.md file."
      : null,
    analysis.wordCount > 0 && analysis.readabilityEase < 50
      ? "Try shorter sentences and simpler words to improve readability."
      : null,
    longSentenceCount > 0
      ? `${longSentenceCount} long sentence${longSentenceCount === 1 ? "" : "s"} could be split for easier reading.`
      : null,
    analysis.fillerMatches.length > 0
      ? "Review filler words to make the writing tighter and more direct."
      : null,
    analysis.repeatedWords.length > 0
      ? "Check repeated words to improve variety and reduce redundancy."
      : null,
  ].filter(Boolean) as string[];

  const statsSummaryText = [
    `Words: ${analysis.wordCount}`,
    `Characters: ${analysis.characterCountWithSpaces}`,
    `Characters without spaces: ${analysis.characterCountWithoutSpaces}`,
    `Sentences: ${analysis.sentenceCount}`,
    `Paragraphs: ${analysis.paragraphCount}`,
    `Reading time: ${formatMinutes(analysis.readingTimeMinutes)}`,
    `Speaking time: ${formatMinutes(analysis.speakingTimeMinutes)}`,
    `Readability: ${analysis.readabilityLabel} (Flesch ${formatDecimal(analysis.readabilityEase, 1)})`,
  ].join("\n");

  const handleCopy = async (value: string, message: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setFeedback(message);
    } catch (_error) {
      setFeedback("Clipboard access is not available in this browser.");
    }
  };

  const handlePaste = async () => {
    try {
      const clipboardText = await navigator.clipboard.readText();
      updateText(
        text
          ? `${text}${text.endsWith("\n") ? "" : "\n"}${clipboardText}`
          : clipboardText,
      );
      setFeedback("Clipboard text pasted into the editor.");
    } catch (_error) {
      setFeedback("Clipboard read permission is not available.");
    }
  };

  const handleClear = () => {
    updateText("");
    setFeedback("Editor cleared.");
  };

  const handleUndo = () => {
    if (!undoStack.length) {
      return;
    }

    const previousValue = undoStack[undoStack.length - 1];
    setUndoStack((current) => current.slice(0, -1));
    setRedoStack((current) => [...current, text]);
    setText(previousValue);
    if (!previousValue.trim()) {
      setTypingStartedAt(null);
    }
  };

  const handleRedo = () => {
    if (!redoStack.length) {
      return;
    }

    const nextValue = redoStack[redoStack.length - 1];
    setRedoStack((current) => current.slice(0, -1));
    setUndoStack((current) => [...current.slice(-(MAX_HISTORY - 1)), text]);
    setText(nextValue);
    if (nextValue.trim()) {
      setTypingStartedAt((current) => current ?? Date.now());
    }
  };

  const handleFileRead = async (file: File) => {
    const isTextFile =
      file.type.startsWith("text/") || /\.(txt|md|markdown)$/i.test(file.name);

    if (!isTextFile) {
      setFeedback("Please upload a .txt, .md or other text-based file.");
      return;
    }

    const fileContent = await file.text();
    updateText(fileContent);
    setFeedback(`${file.name} loaded into the editor.`);
  };

  const handleTextTransform = (
    transform: (value: string) => string,
    message: string,
  ) => {
    const nextValue = transform(text);
    updateText(nextValue);
    setFeedback(message);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        backgroundColor: pageBackground,
        color: primaryTextColor,
      }}
    >
      <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 3 }, flex: 1 }}>
        <Stack spacing={3}>
          <Box>
            <Typography
              component="h1"
              variant="h4"
              sx={{ fontWeight: 800, mb: 1 }}
            >
              Free Word Counter & Text Analyzer
            </Typography>
            <Typography
              variant="body1"
              color={mutedTextColor}
              sx={{ maxWidth: 1024 }}
            >
              Count words, characters, sentences, paragraphs and reading time in
              real time while you type or paste. Analyze keyword density,
              readability, repeated words, filler terms, typing speed and
              document structure without sending your text to a server.
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              useFlexGap
              flexWrap="wrap"
              sx={{ mt: 1.5 }}
            >
              <Chip
                size="small"
                color="primary"
                variant="outlined"
                label="Client-side only"
              />
              <Chip size="small" variant="outlined" label="Real-time stats" />
              <Chip size="small" variant="outlined" label="Keyword density" />
              <Chip
                size="small"
                variant="outlined"
                label="Readability insights"
              />
              <Chip size="small" variant="outlined" label="Mobile friendly" />
            </Stack>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr 1fr",
                sm: "repeat(4, minmax(0, 1fr))",
              },
              gap: 1.5,
            }}
          >
            {summaryCards.map((item) => (
              <Card
                key={item.label}
                variant="outlined"
                sx={{
                  backgroundColor: cardBackground,
                  borderColor,
                  color: primaryTextColor,
                  overflow: "hidden",
                }}
              >
                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                  <Stack spacing={1.25}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: 2,
                          display: "grid",
                          placeItems: "center",
                          backgroundColor: isDarkMode
                            ? "rgba(145, 189, 58, 0.12)"
                            : "rgba(145, 189, 58, 0.08)",
                        }}
                      >
                        {item.icon}
                      </Box>
                      <Typography variant="caption" color={mutedTextColor}>
                        {item.label}
                      </Typography>
                    </Stack>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 800, lineHeight: 1.2 }}
                    >
                      {item.value}
                    </Typography>
                    <Typography variant="caption" color={mutedTextColor}>
                      {item.meta}
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "minmax(0, 1.45fr) minmax(360px, 420px)",
              },
              gap: 2,
              alignItems: "start",
            }}
          >
            <Stack spacing={2}>
              <Card
                variant="outlined"
                sx={{
                  backgroundColor: cardBackground,
                  borderColor,
                  color: primaryTextColor,
                }}
              >
                <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                  <Stack spacing={2}>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1}
                      alignItems={{ xs: "stretch", sm: "center" }}
                      justifyContent="space-between"
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <NotesIcon color="primary" />
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 700 }}
                        >
                          Editor
                        </Typography>
                      </Stack>
                      <Stack
                        direction="row"
                        spacing={1}
                        useFlexGap
                        flexWrap="wrap"
                      >
                        <FormControlLabel
                          control={
                            <Switch
                              checked={saveDraftEnabled}
                              onChange={(event) =>
                                setSaveDraftEnabled(event.target.checked)
                              }
                            />
                          }
                          label="Save draft locally"
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={isDarkMode}
                              onChange={(event) =>
                                setIsDarkMode(event.target.checked)
                              }
                            />
                          }
                          label={isDarkMode ? "Dark mode" : "Light mode"}
                        />
                      </Stack>
                    </Stack>

                    <Stack
                      direction="row"
                      spacing={1}
                      useFlexGap
                      flexWrap="wrap"
                    >
                      <Button
                        startIcon={<ContentPasteIcon />}
                        variant="outlined"
                        onClick={handlePaste}
                      >
                        Paste
                      </Button>
                      <Button
                        startIcon={<ContentCopyIcon />}
                        variant="outlined"
                        onClick={() =>
                          handleCopy(text, "Text copied to the clipboard.")
                        }
                        disabled={!text}
                      >
                        Copy
                      </Button>
                      <Button
                        startIcon={<UndoIcon />}
                        variant="outlined"
                        onClick={handleUndo}
                        disabled={!undoStack.length}
                      >
                        Undo
                      </Button>
                      <Button
                        startIcon={<RedoIcon />}
                        variant="outlined"
                        onClick={handleRedo}
                        disabled={!redoStack.length}
                      >
                        Redo
                      </Button>
                      <Button
                        startIcon={<DeleteOutlineIcon />}
                        variant="outlined"
                        color="error"
                        onClick={handleClear}
                        disabled={!text}
                      >
                        Clear
                      </Button>
                      <Button
                        startIcon={<InsightsIcon />}
                        variant="outlined"
                        onClick={() =>
                          handleCopy(
                            statsSummaryText,
                            "Text statistics summary copied.",
                          )
                        }
                        disabled={!analysis.wordCount}
                      >
                        Copy stats
                      </Button>
                    </Stack>

                    <Stack spacing={1}>
                      <Typography variant="body2" color={mutedTextColor}>
                        Start faster with a sample:
                      </Typography>
                      <Stack
                        direction="row"
                        spacing={1}
                        useFlexGap
                        flexWrap="wrap"
                      >
                        {starterTemplates.map((template) => (
                          <Chip
                            key={template.label}
                            clickable
                            color="primary"
                            variant="outlined"
                            label={template.label}
                            onClick={() => {
                              updateText(template.text);
                              setFeedback(`${template.label} template loaded.`);
                            }}
                          />
                        ))}
                      </Stack>
                    </Stack>

                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr 1fr",
                          sm: "repeat(4, minmax(0, 1fr))",
                        },
                        gap: 1,
                      }}
                    >
                      {[
                        {
                          label: "Words",
                          value: formatDecimal(analysis.wordCount, 0),
                        },
                        {
                          label: "Reading",
                          value: formatMinutes(analysis.readingTimeMinutes),
                        },
                        {
                          label: "Language",
                          value: analysis.language,
                        },
                        {
                          label: "Typing",
                          value: `${formatDecimal(typingSpeedWpm, 1)} WPM`,
                        },
                      ].map((item) => (
                        <Box
                          key={item.label}
                          sx={{
                            p: 1.1,
                            borderRadius: 2,
                            border: "1px solid",
                            borderColor,
                            backgroundColor: isDarkMode
                              ? "rgba(30, 41, 59, 0.52)"
                              : "background.default",
                          }}
                        >
                          <Typography variant="caption" color={mutedTextColor}>
                            {item.label}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 700, mt: 0.4, lineHeight: 1.35 }}
                          >
                            {item.value}
                          </Typography>
                        </Box>
                      ))}
                    </Box>

                    <Box
                      sx={{
                        border: "1px dashed",
                        borderColor,
                        borderRadius: 3,
                        p: { xs: 1.5, sm: 2 },
                        backgroundColor: isDarkMode
                          ? "rgba(15, 23, 42, 0.65)"
                          : "background.default",
                      }}
                      onDragOver={(event) => {
                        event.preventDefault();
                      }}
                      onDrop={async (event) => {
                        event.preventDefault();
                        const file = event.dataTransfer.files?.[0];
                        if (file) {
                          await handleFileRead(file);
                        }
                      }}
                    >
                      <Stack spacing={1.5}>
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={1}
                          justifyContent="space-between"
                          alignItems={{ xs: "flex-start", sm: "center" }}
                        >
                          <Typography variant="body2" color={mutedTextColor}>
                            Type, paste, or drag and drop a .txt or .md file.
                            Everything updates live.
                          </Typography>
                          <Button
                            component="label"
                            variant="outlined"
                            startIcon={<UploadFileIcon />}
                          >
                            Upload text file
                            <Box
                              component="input"
                              type="file"
                              accept=".txt,.md,.markdown,text/plain,text/markdown"
                              sx={{ display: "none" }}
                              onChange={async (
                                event: React.ChangeEvent<HTMLInputElement>,
                              ) => {
                                const file = event.target.files?.[0];
                                if (file) {
                                  await handleFileRead(file);
                                }
                                event.target.value = "";
                              }}
                            />
                          </Button>
                        </Stack>

                        <TextField
                          multiline
                          fullWidth
                          minRows={18}
                          maxRows={28}
                          placeholder="Start typing or paste your text here..."
                          value={text}
                          onChange={(event) => updateText(event.target.value)}
                          variant="outlined"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              alignItems: "flex-start",
                              fontFamily: '"Inter", system-ui, sans-serif',
                              fontSize: "0.98rem",
                              lineHeight: 1.7,
                              backgroundColor: isDarkMode
                                ? "rgba(15, 23, 42, 0.82)"
                                : "background.paper",
                              color: primaryTextColor,
                            },
                            "& textarea": {
                              color: primaryTextColor,
                            },
                          }}
                        />

                        {!text.trim() && (
                          <Box
                            sx={{
                              p: 1.5,
                              borderRadius: 2,
                              border: "1px solid",
                              borderColor,
                              backgroundColor: isDarkMode
                                ? "rgba(30, 41, 59, 0.45)"
                                : "rgba(145, 189, 58, 0.05)",
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600, mb: 0.5 }}
                            >
                              New here?
                            </Typography>
                            <Typography variant="body2" color={mutedTextColor}>
                              Paste an article, type from scratch, or load a
                              sample above to see live counts, readability,
                              keyword density, and writing suggestions.
                            </Typography>
                          </Box>
                        )}
                      </Stack>
                    </Box>

                    <Stack
                      direction="row"
                      spacing={1}
                      useFlexGap
                      flexWrap="wrap"
                    >
                      <Chip
                        size="small"
                        icon={<SaveOutlinedIcon />}
                        label={
                          saveDraftEnabled
                            ? savedAt
                              ? `Draft saved locally ${new Date(savedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`
                              : "Local draft saving enabled"
                            : "Local draft saving disabled"
                        }
                        variant="outlined"
                      />
                      <Chip
                        size="small"
                        icon={isDarkMode ? <DarkModeIcon /> : <LightModeIcon />}
                        label={isDarkMode ? "Dark editor" : "Light editor"}
                        variant="outlined"
                      />
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>

              <Card
                variant="outlined"
                sx={{
                  backgroundColor: cardBackground,
                  borderColor,
                  color: primaryTextColor,
                }}
              >
                <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                  <Stack spacing={2}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Text transformations
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={1}
                      useFlexGap
                      flexWrap="wrap"
                    >
                      <Button
                        variant="outlined"
                        onClick={() =>
                          handleTextTransform(
                            (value) => value.toUpperCase(),
                            "Converted text to uppercase.",
                          )
                        }
                        disabled={!text}
                      >
                        UPPERCASE
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() =>
                          handleTextTransform(
                            (value) => value.toLowerCase(),
                            "Converted text to lowercase.",
                          )
                        }
                        disabled={!text}
                      >
                        lowercase
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() =>
                          handleTextTransform(
                            toTitleCase,
                            "Converted text to title case.",
                          )
                        }
                        disabled={!text}
                      >
                        Title Case
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() =>
                          handleTextTransform(
                            toSentenceCase,
                            "Converted text to sentence case.",
                          )
                        }
                        disabled={!text}
                      >
                        Sentence case
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() =>
                          handleTextTransform(
                            toCamelCase,
                            "Converted text to camelCase.",
                          )
                        }
                        disabled={!text}
                      >
                        camelCase
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() =>
                          handleTextTransform(
                            toSnakeCase,
                            "Converted text to snake_case.",
                          )
                        }
                        disabled={!text}
                      >
                        snake_case
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() =>
                          handleTextTransform(
                            removeExtraSpaces,
                            "Removed extra spaces and cleaned paragraph gaps.",
                          )
                        }
                        disabled={!text}
                      >
                        Remove extra spaces
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() =>
                          handleTextTransform(
                            removeLineBreaks,
                            "Removed line breaks from the text.",
                          )
                        }
                        disabled={!text}
                      >
                        Remove line breaks
                      </Button>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>

              <Card
                variant="outlined"
                sx={{
                  backgroundColor: cardBackground,
                  borderColor,
                  color: primaryTextColor,
                }}
              >
                <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                  <Stack spacing={2}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Export & sharing
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={1}
                      useFlexGap
                      flexWrap="wrap"
                    >
                      <Button
                        variant="contained"
                        startIcon={<ContentCopyIcon />}
                        onClick={() =>
                          handleCopy(text, "Full text copied to the clipboard.")
                        }
                        disabled={!text}
                      >
                        Copy text
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={() => downloadFile(text, "txt")}
                        disabled={!text}
                      >
                        Download .txt
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={() => downloadFile(text, "md")}
                        disabled={!text}
                      >
                        Download .md
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<PrintIcon />}
                        onClick={() => {
                          const printed = printText(text);
                          if (!printed) {
                            setFeedback(
                              "Unable to open the print preview window.",
                            );
                          }
                        }}
                        disabled={!text}
                      >
                        Print
                      </Button>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>

              <Card
                variant="outlined"
                sx={{
                  backgroundColor: cardBackground,
                  borderColor,
                  color: primaryTextColor,
                }}
              >
                <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                  <Stack spacing={2}>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1}
                      alignItems={{ xs: "stretch", sm: "center" }}
                      justifyContent="space-between"
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        Highlight preview
                      </Typography>
                      <Stack
                        direction="row"
                        spacing={1}
                        useFlexGap
                        flexWrap="wrap"
                      >
                        <FormControlLabel
                          control={
                            <Switch
                              checked={highlightRepeatedWords}
                              onChange={(event) =>
                                setHighlightRepeatedWords(event.target.checked)
                              }
                              size="small"
                            />
                          }
                          label="Repeated"
                          sx={{
                            m: 0,
                            px: 1,
                            py: 0.25,
                            borderRadius: 2,
                            border: "1px solid",
                            borderColor: HIGHLIGHT_COLORS.repeated.strong,
                            backgroundColor: HIGHLIGHT_COLORS.repeated.soft,
                          }}
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={highlightKeywords}
                              onChange={(event) =>
                                setHighlightKeywords(event.target.checked)
                              }
                              size="small"
                            />
                          }
                          label="Keywords"
                          sx={{
                            m: 0,
                            px: 1,
                            py: 0.25,
                            borderRadius: 2,
                            border: "1px solid",
                            borderColor: HIGHLIGHT_COLORS.keyword.strong,
                            backgroundColor: HIGHLIGHT_COLORS.keyword.soft,
                          }}
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={highlightFillerWords}
                              onChange={(event) =>
                                setHighlightFillerWords(event.target.checked)
                              }
                              size="small"
                            />
                          }
                          label="Filler"
                          sx={{
                            m: 0,
                            px: 1,
                            py: 0.25,
                            borderRadius: 2,
                            border: "1px solid",
                            borderColor: HIGHLIGHT_COLORS.filler.strong,
                            backgroundColor: HIGHLIGHT_COLORS.filler.soft,
                          }}
                        />
                        <FormControlLabel
                          control={
                            <Switch
                              checked={highlightLongSentences}
                              onChange={(event) =>
                                setHighlightLongSentences(event.target.checked)
                              }
                              size="small"
                            />
                          }
                          label="Long sentences"
                          sx={{
                            m: 0,
                            px: 1,
                            py: 0.25,
                            borderRadius: 2,
                            border: "1px solid",
                            borderColor: HIGHLIGHT_COLORS.longSentence.strong,
                            backgroundColor: HIGHLIGHT_COLORS.longSentence.soft,
                          }}
                        />
                      </Stack>
                    </Stack>

                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 3,
                        border: "1px solid",
                        borderColor,
                        minHeight: 180,
                        maxHeight: 340,
                        overflow: "auto",
                        lineHeight: 1.8,
                        backgroundColor: isDarkMode
                          ? "rgba(15, 23, 42, 0.68)"
                          : "background.default",
                      }}
                    >
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ whiteSpace: "pre-wrap" }}
                      >
                        {highlightPreview}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color={mutedTextColor}>
                      Each highlight type now uses its own dedicated color. If a
                      word matches multiple types, the mark blends those colors
                      so the overlap stays visible.
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={1}
                      useFlexGap
                      flexWrap="wrap"
                    >
                      {[
                        {
                          label: "Repeated",
                          color: HIGHLIGHT_COLORS.repeated,
                        },
                        {
                          label: "Keyword",
                          color: HIGHLIGHT_COLORS.keyword,
                        },
                        {
                          label: "Filler",
                          color: HIGHLIGHT_COLORS.filler,
                        },
                        {
                          label: "Long sentence",
                          color: HIGHLIGHT_COLORS.longSentence,
                        },
                      ].map((item) => (
                        <Box
                          key={item.label}
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.75,
                            px: 1,
                            py: 0.6,
                            borderRadius: 999,
                            border: "1px solid",
                            borderColor: item.color.strong,
                            backgroundColor: item.color.soft,
                          }}
                        >
                          <Box
                            sx={{
                              width: 10,
                              height: 10,
                              borderRadius: 999,
                              backgroundColor: item.color.strong,
                            }}
                          />
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: 700 }}
                          >
                            {item.label}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>

              {feedback && (
                <Alert severity="success" onClose={() => setFeedback(null)}>
                  {feedback}
                </Alert>
              )}

              {smartSuggestions.length > 0 && (
                <Alert severity="info">
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 700, mb: 0.75 }}
                  >
                    Quick suggestions
                  </Typography>
                  <Stack spacing={0.5}>
                    {smartSuggestions.slice(0, 4).map((item) => (
                      <Typography key={item} variant="body2">
                        • {item}
                      </Typography>
                    ))}
                  </Stack>
                </Alert>
              )}
            </Stack>

            <Stack
              spacing={2}
              sx={{
                position: { sm: "sticky" },
                top: { sm: 88 },
                alignSelf: "start",
              }}
            >
              <Card
                variant="outlined"
                sx={{
                  backgroundColor: cardBackground,
                  borderColor,
                  color: primaryTextColor,
                }}
              >
                <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                  <Stack spacing={2}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Live counts
                    </Typography>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                        gap: 1.25,
                      }}
                    >
                      {coreStats.map((stat) => (
                        <Box
                          key={stat.label}
                          sx={{
                            p: 1.25,
                            borderRadius: 2,
                            border: "1px solid",
                            borderColor,
                            backgroundColor: isDarkMode
                              ? "rgba(30, 41, 59, 0.72)"
                              : "background.default",
                          }}
                        >
                          <Typography variant="caption" color={mutedTextColor}>
                            {stat.label}
                          </Typography>
                          <Typography
                            variant="h6"
                            sx={{ fontWeight: 800, mt: 0.4 }}
                          >
                            {stat.value}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Stack>
                </CardContent>
              </Card>

              <Card
                variant="outlined"
                sx={{
                  backgroundColor: cardBackground,
                  borderColor,
                  color: primaryTextColor,
                }}
              >
                <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                  <Stack spacing={1.5}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Reading analytics
                    </Typography>
                    <Stack spacing={1}>
                      <Typography variant="body2" color={mutedTextColor}>
                        Estimated reading time:{" "}
                        <strong>
                          {formatMinutes(analysis.readingTimeMinutes)}
                        </strong>
                      </Typography>
                      <Typography variant="body2" color={mutedTextColor}>
                        Estimated speaking time:{" "}
                        <strong>
                          {formatMinutes(analysis.speakingTimeMinutes)}
                        </strong>
                      </Typography>
                      <Typography variant="body2" color={mutedTextColor}>
                        Typing duration:{" "}
                        <strong>{formatDuration(typingDurationSeconds)}</strong>
                      </Typography>
                      <Typography variant="body2" color={mutedTextColor}>
                        Approx. typing speed:{" "}
                        <strong>{formatDecimal(typingSpeedWpm, 1)} WPM</strong>
                      </Typography>
                      <Typography variant="body2" color={mutedTextColor}>
                        Detected language: <strong>{analysis.language}</strong>
                      </Typography>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>

              <Card
                variant="outlined"
                sx={{
                  backgroundColor: cardBackground,
                  borderColor,
                  color: primaryTextColor,
                }}
              >
                <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <FlagIcon color="primary" />
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        Writing goal tracker
                      </Typography>
                    </Stack>
                    <TextField
                      fullWidth
                      label="Target word count"
                      type="number"
                      value={targetWords}
                      onChange={(event) => setTargetWords(event.target.value)}
                      inputProps={{ min: 1 }}
                    />
                    <Stack
                      direction="row"
                      spacing={1}
                      useFlexGap
                      flexWrap="wrap"
                    >
                      {[300, 500, 1000, 1500].map((value) => (
                        <Chip
                          key={value}
                          clickable
                          size="small"
                          variant={
                            targetWordValue === value ? "filled" : "outlined"
                          }
                          color={
                            targetWordValue === value ? "primary" : "default"
                          }
                          label={`${value} words`}
                          onClick={() => setTargetWords(String(value))}
                        />
                      ))}
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={goalProgress}
                      sx={{ height: 10, borderRadius: 999 }}
                    />
                    <Stack
                      direction="row"
                      spacing={1}
                      useFlexGap
                      flexWrap="wrap"
                    >
                      <Chip
                        size="small"
                        color={goalProgress >= 100 ? "success" : "primary"}
                        variant={goalProgress >= 100 ? "filled" : "outlined"}
                        label={
                          targetWordValue > 0
                            ? `${formatPercent(goalProgress)} complete`
                            : "No goal set"
                        }
                      />
                      {targetWordValue > 0 && (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={`${Math.max(targetWordValue - analysis.wordCount, 0)} words left`}
                        />
                      )}
                    </Stack>
                    <Typography variant="body2" color={mutedTextColor}>
                      {targetWordValue > 0
                        ? `${analysis.wordCount} of ${targetWordValue} words • ${formatPercent(goalProgress)}`
                        : "Enter a positive target to track progress."}
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>

              <Card
                variant="outlined"
                sx={{
                  backgroundColor: cardBackground,
                  borderColor,
                  color: primaryTextColor,
                }}
              >
                <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <InsightsIcon color="primary" />
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        Keyword density
                      </Typography>
                    </Stack>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={filterStopWords}
                          onChange={(event) =>
                            setFilterStopWords(event.target.checked)
                          }
                        />
                      }
                      label="Filter common stop words"
                    />
                    {analysis.keywordEntries.length ? (
                      <Stack spacing={1}>
                        {analysis.keywordEntries.map((item) => (
                          <Box
                            key={item.word}
                            sx={{
                              display: "grid",
                              gridTemplateColumns: "minmax(0, 1fr) auto auto",
                              gap: 1,
                              alignItems: "center",
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600, wordBreak: "break-word" }}
                            >
                              {item.word}
                            </Typography>
                            <Chip
                              size="small"
                              variant="outlined"
                              label={`${item.count}`}
                            />
                            <Typography
                              variant="caption"
                              color={mutedTextColor}
                            >
                              {formatPercent(item.percentage)}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    ) : (
                      <Typography variant="body2" color={mutedTextColor}>
                        Add more text to see keyword frequency and percentages.
                      </Typography>
                    )}
                  </Stack>
                </CardContent>
              </Card>

              <Card
                variant="outlined"
                sx={{
                  backgroundColor: cardBackground,
                  borderColor,
                  color: primaryTextColor,
                }}
              >
                <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                  <Stack spacing={1.5}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Writing insights
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={1}
                      useFlexGap
                      flexWrap="wrap"
                    >
                      <Chip
                        size="small"
                        color={
                          analysis.readabilityEase >= 60
                            ? "success"
                            : analysis.readabilityEase >= 40
                              ? "warning"
                              : "error"
                        }
                        label={`${analysis.readabilityLabel} readability`}
                      />
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`Flesch ${formatDecimal(analysis.readabilityEase, 1)}`}
                      />
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`Grade ${formatDecimal(analysis.readabilityGrade, 1)}`}
                      />
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`Lexical diversity ${formatPercent(analysis.lexicalDiversity)}`}
                      />
                    </Stack>
                    <Typography variant="body2" color={mutedTextColor}>
                      Unique words: <strong>{analysis.uniqueWordCount}</strong>
                    </Typography>
                    <Typography variant="body2" color={mutedTextColor}>
                      Repeated adjacent words:{" "}
                      <strong>{analysis.repeatedAdjacentWords.length}</strong>
                    </Typography>
                    <Typography variant="body2" color={mutedTextColor}>
                      Longest sentence:{" "}
                      <strong>{analysis.longestSentenceWords} words</strong>
                    </Typography>
                    {analysis.fillerMatches.length ? (
                      <Stack spacing={0.75}>
                        <Typography variant="body2" color={mutedTextColor}>
                          Filler words detected:
                        </Typography>
                        {analysis.fillerMatches.slice(0, 8).map((item) => (
                          <Typography
                            key={item.term}
                            variant="body2"
                            color={mutedTextColor}
                          >
                            • {item.term} — {item.count}
                          </Typography>
                        ))}
                      </Stack>
                    ) : (
                      <Typography variant="body2" color={mutedTextColor}>
                        No common filler words detected from the built-in list.
                      </Typography>
                    )}
                  </Stack>
                </CardContent>
              </Card>

              <Card
                variant="outlined"
                sx={{
                  backgroundColor: cardBackground,
                  borderColor,
                  color: primaryTextColor,
                }}
              >
                <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                  <Stack spacing={1.5}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Structure summary
                    </Typography>
                    <Typography variant="body2" color={mutedTextColor}>
                      Average paragraph length:{" "}
                      <strong>
                        {formatDecimal(analysis.averageParagraphLength, 1)}{" "}
                        words
                      </strong>
                    </Typography>
                    <Typography variant="body2" color={mutedTextColor}>
                      Longest paragraph:{" "}
                      <strong>{analysis.longestParagraphWords} words</strong>
                    </Typography>
                    <Typography variant="body2" color={mutedTextColor}>
                      Short paragraphs:{" "}
                      <strong>
                        {analysis.structureSummary.shortParagraphs}
                      </strong>
                    </Typography>
                    <Typography variant="body2" color={mutedTextColor}>
                      Medium paragraphs:{" "}
                      <strong>
                        {analysis.structureSummary.mediumParagraphs}
                      </strong>
                    </Typography>
                    <Typography variant="body2" color={mutedTextColor}>
                      Long paragraphs:{" "}
                      <strong>
                        {analysis.structureSummary.longParagraphs}
                      </strong>
                    </Typography>
                    <Divider />
                    <Typography variant="body2" color={mutedTextColor}>
                      Longest words:{" "}
                      <strong>{analysis.longestWords.join(", ") || "—"}</strong>
                    </Typography>
                    <Typography variant="body2" color={mutedTextColor}>
                      Shortest words:{" "}
                      <strong>
                        {analysis.shortestWords.join(", ") || "—"}
                      </strong>
                    </Typography>
                    {analysis.repeatedWords.length ? (
                      <Box>
                        <Typography
                          variant="body2"
                          color={mutedTextColor}
                          sx={{ mb: 0.5 }}
                        >
                          Most repeated words:
                        </Typography>
                        <Stack
                          direction="row"
                          spacing={1}
                          useFlexGap
                          flexWrap="wrap"
                        >
                          {analysis.repeatedWords.slice(0, 8).map((item) => (
                            <Chip
                              key={item.word}
                              size="small"
                              variant="outlined"
                              label={`${item.word} · ${item.count}`}
                            />
                          ))}
                        </Stack>
                      </Box>
                    ) : (
                      <Typography variant="body2" color={mutedTextColor}>
                        Repeated words will appear here once the text is long
                        enough.
                      </Typography>
                    )}
                  </Stack>
                </CardContent>
              </Card>

              <Alert severity="info" sx={{ alignItems: "flex-start" }}>
                Privacy notice: all text processing happens locally in your
                browser. No content is sent to servers for counting, keyword
                analysis, readability checks or draft saving.
              </Alert>
            </Stack>
          </Box>

          <Box component="section">
            <Card
              variant="outlined"
              sx={{
                backgroundColor: cardBackground,
                borderColor,
                color: primaryTextColor,
              }}
            >
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Stack spacing={3}>
                  <Box>
                    <Typography
                      component="h2"
                      variant="h5"
                      sx={{ fontWeight: 800, mb: 1 }}
                    >
                      Word counter for writers, marketers, students and teams
                    </Typography>
                    <Typography variant="body2" color={mutedTextColor}>
                      This page works as a free online word counter, character
                      counter and text analysis utility. It is useful for blog
                      drafts, landing pages, assignment writing, script timing,
                      SEO content reviews, speech preparation and general
                      editing workflows.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                      gap: 3,
                    }}
                  >
                    <Box>
                      <Typography
                        component="h3"
                        variant="subtitle1"
                        sx={{ fontWeight: 700, mb: 1.25 }}
                      >
                        Popular use cases
                      </Typography>
                      <Stack spacing={1}>
                        {wordCounterUseCases.map((item) => (
                          <Typography
                            key={item}
                            variant="body2"
                            color={mutedTextColor}
                          >
                            • {item}
                          </Typography>
                        ))}
                      </Stack>
                    </Box>

                    <Box>
                      <Typography
                        component="h3"
                        variant="subtitle1"
                        sx={{ fontWeight: 700, mb: 1.25 }}
                      >
                        Why this tool is useful
                      </Typography>
                      <Stack spacing={1}>
                        <Typography variant="body2" color={mutedTextColor}>
                          • Runs completely client-side for better privacy.
                        </Typography>
                        <Typography variant="body2" color={mutedTextColor}>
                          • Combines counts, keyword analysis, readability and
                          cleanup tools in one place.
                        </Typography>
                        <Typography variant="body2" color={mutedTextColor}>
                          • Supports live goal tracking, uploads, exports and
                          mobile-friendly editing.
                        </Typography>
                        <Typography variant="body2" color={mutedTextColor}>
                          • Helps optimize writing for clarity, timing, SEO and
                          structure.
                        </Typography>
                      </Stack>
                    </Box>
                  </Box>

                  <Divider />

                  <Box>
                    <Typography
                      component="h2"
                      variant="h5"
                      sx={{ fontWeight: 800, mb: 2 }}
                    >
                      Frequently asked questions
                    </Typography>
                    <Stack spacing={2}>
                      {wordCounterFaqItems.map((item) => (
                        <Box key={item.question}>
                          <Typography
                            component="h3"
                            variant="subtitle1"
                            sx={{ fontWeight: 700, mb: 0.5 }}
                          >
                            {item.question}
                          </Typography>
                          <Typography variant="body2" color={mutedTextColor}>
                            {item.answer}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Box>
        </Stack>
      </Container>

      <Footer />
    </Box>
  );
};

export default WordCounter;
